import type { ApiNode } from '@goalbuddy/client';
import { canHaveParent, type NodeType } from '@goalbuddy/core';
import { useMutation, useQuery } from '@tanstack/react-query';
import { ListChecks, RotateCcw } from 'lucide-react';
import { useState } from 'react';
import { Navigate, useParams, useSearchParams } from 'react-router';
import { InboxTriage } from '@/components/inbox-triage';
import { NodeList } from '@/components/node-list';
import { TypeIcon } from '@/components/node-meta';
import { Button, Empty, Input, PageHeader, Select, Spinner } from '@/components/ui';
import { api } from '@/lib/api';
import { useDetail, useNodes, useStructure, useUsers } from '@/lib/hooks';
import { fmtDateTime } from '@/lib/utils';

export function NextActionsPage() {
  const [f, setF] = useState({ context: '', energy: '', max_effort: '', tag: '', assignee: '' });
  const { data: tags } = useQuery({ queryKey: ['tags'], queryFn: api.tags });
  const { data: users } = useUsers();
  const { data, isLoading } = useNodes({
    type: ['task'],
    is_next_action: true,
    context: f.context || undefined,
    energy: (f.energy || undefined) as 'low' | 'med' | 'high' | undefined,
    max_effort: f.max_effort ? Number(f.max_effort) : undefined,
    tag: f.tag || undefined,
    assignee: f.assignee || undefined,
  });
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) =>
    setF({ ...f, [k]: e.target.value });
  return (
    <div>
      <PageHeader title="Next actions" subtitle="Planning view of every next action" />
      <div className="mb-4 grid grid-cols-2 gap-2 sm:grid-cols-5">
        <Select value={f.context} onChange={set('context')} aria-label="Context">
          <option value="">Any context</option>
          {['@computer', '@phone', '@meran', '@errands', '@home'].map((c) => (
            <option key={c}>{c}</option>
          ))}
        </Select>
        <Select value={f.energy} onChange={set('energy')} aria-label="Energy">
          <option value="">Any energy</option>
          <option value="low">low</option>
          <option value="med">med</option>
          <option value="high">high</option>
        </Select>
        <Select value={f.max_effort} onChange={set('max_effort')} aria-label="Time available">
          <option value="">Any time</option>
          {[5, 15, 30, 60, 120].map((m) => (
            <option key={m} value={m}>
              ≤ {m} min
            </option>
          ))}
        </Select>
        <Select value={f.tag} onChange={set('tag')} aria-label="Tag">
          <option value="">Any tag</option>
          {tags?.map((t) => (
            <option key={t.name}>{t.name}</option>
          ))}
        </Select>
        <Select value={f.assignee} onChange={set('assignee')} aria-label="Assignee">
          <option value="">Anyone</option>
          {users
            ?.filter((u) => u.role !== 'system')
            .map((u) => (
              <option key={u.id} value={u.name}>
                {u.name}
              </option>
            ))}
        </Select>
      </div>
      <NodeList nodes={data} loading={isLoading} empty="No next actions match these filters." />
    </div>
  );
}

function InboxActions({ node }: { node: ApiNode }) {
  const { data: structure } = useStructure();
  const move = useMutation({ mutationFn: (parent: number) => api.moveNode(node.id, parent) });
  const convert = useMutation({ mutationFn: (t: 'task' | 'goal') => api.convertNode(node.id, t) });
  const targets = (structure ?? []).filter(
    (p) =>
      ['area', 'goal', 'result', 'project', 'section', 'list'].includes(p.type) &&
      canHaveParent(node.type, p.type as NodeType),
  );
  return (
    <div className="flex shrink-0 items-center gap-1">
      {node.type === 'idea' && (
        <Button size="sm" variant="outline" onClick={() => convert.mutate('task')}>
          → task
        </Button>
      )}
      <Select
        className="h-7 w-40 text-xs"
        value=""
        onChange={(e) => e.target.value && move.mutate(Number(e.target.value))}
        aria-label="Move to"
      >
        <option value="">Move to…</option>
        {targets.map((t) => (
          <option key={t.id} value={t.id}>
            {t.type}: {t.title}
          </option>
        ))}
      </Select>
    </div>
  );
}

export function InboxPage() {
  const { data, isLoading } = useNodes({ parent: 'none', type: ['task', 'idea'] });
  const [triage, setTriage] = useState(false);
  return (
    <div>
      <PageHeader title="Inbox" subtitle="Tasks and ideas without a parent. Press c to capture.">
        {!!data?.length && !triage && (
          <Button onClick={() => setTriage(true)}>
            <ListChecks size={14} /> Triage {data.length}
          </Button>
        )}
      </PageHeader>
      {triage ? (
        <InboxTriage items={data ?? []} onExit={() => setTriage(false)} />
      ) : (
        <NodeList
          nodes={data}
          loading={isLoading}
          empty="Inbox is empty."
          extra={(n) => <InboxActions node={n} />}
        />
      )}
    </div>
  );
}

export function QuestionsPage() {
  const { data, isLoading } = useQuery({ queryKey: ['questions'], queryFn: api.questions });
  const { open } = useDetail();
  if (isLoading) return <Spinner />;
  return (
    <div>
      <PageHeader
        title="Questions"
        subtitle="Open questions and items that need clarification. The agent asks these in the next session."
      />
      {!data?.length && <Empty>No open questions.</Empty>}
      <ul className="space-y-2">
        {data?.map((q) => (
          <li
            key={q.id}
            className="rounded-xl border border-border bg-surface p-3.5 shadow-[var(--shadow-card)]"
          >
            <button
              className="flex items-center gap-2 font-medium hover:underline"
              onClick={() => open(q.id)}
            >
              <TypeIcon type={q.type} /> {q.title}
            </button>
            {q.open_question && <div className="mt-1 text-sm">{q.open_question}</div>}
            {q.missing_completion_criterion && (
              <div className="mt-1 text-sm text-warn">Result has no completion criterion.</div>
            )}
            {q.needs_clarification && !q.open_question && (
              <div className="mt-1 text-sm text-muted-fg">Marked as needing clarification.</div>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

export function SearchPage() {
  const [params, setParams] = useSearchParams();
  const q = params.get('q') ?? '';
  const [text, setText] = useState(q);
  const { data, isLoading } = useNodes({ q, include_done: true }, q.length > 0);
  return (
    <div>
      <PageHeader
        title="Search"
        subtitle="Titles and short descriptions, including done and dropped. Archived items are excluded."
      />
      <form
        className="mb-4 flex gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          setParams(text ? { q: text } : {});
        }}
      >
        <Input autoFocus value={text} onChange={(e) => setText(e.target.value)} placeholder="Search…" />
        <Button type="submit">Search</Button>
      </form>
      {q && <NodeList nodes={data} loading={isLoading} empty={`No results for “${q}”.`} />}
    </div>
  );
}

export function ArchivePage() {
  const { data, isLoading } = useNodes({ archived: true, include_done: true });
  const restore = useMutation({ mutationFn: (id: number) => api.restoreNode(id) });
  if (isLoading) return <Spinner />;
  const archivedIds = new Set((data ?? []).map((n) => n.id));
  // Show only subtree roots: archived nodes whose parent is not archived.
  const roots = (data ?? []).filter((n) => n.parent_id === null || !archivedIds.has(n.parent_id));
  const count = (id: number): number =>
    (data ?? []).filter((n) => n.parent_id === id).reduce((s, c) => s + 1 + count(c.id), 0);
  return (
    <div>
      <PageHeader title="Archive" subtitle="Archived subtrees. Not shown in any other view or search." />
      {!roots.length && <Empty>The archive is empty.</Empty>}
      <ul className="space-y-1">
        {roots.map((n) => (
          <li
            key={n.id}
            className="flex items-center justify-between gap-2 rounded-xl border border-border bg-surface px-3.5 py-2.5 shadow-[var(--shadow-card)]"
          >
            <div className="flex min-w-0 items-center gap-2">
              <TypeIcon type={n.type} />
              <span className="truncate">{n.title}</span>
              <span className="text-xs text-muted-fg">
                +{count(n.id)} children · archived {fmtDateTime(n.archived_at)}
              </span>
            </div>
            <Button size="sm" variant="outline" onClick={() => restore.mutate(n.id)}>
              <RotateCcw size={13} /> Restore
            </Button>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** A saved view is a stored filter for GET /nodes; `special` filters map to dedicated pages. */
export function SavedViewPage() {
  const id = Number(useParams().id);
  const { data: views } = useQuery({ queryKey: ['views'], queryFn: api.views });
  const view = views?.find((v) => v.id === id);
  const filter = (view?.filter ?? {}) as Record<string, unknown>;
  const { data, isLoading } = useNodes(filter as never, !!view && !filter.special);
  if (!views) return <Spinner />;
  if (!view) return <Empty>View not found.</Empty>;
  if (filter.special === 'today') return <Navigate to="/today" replace />;
  if (filter.special === 'questions') return <Navigate to="/questions" replace />;
  return (
    <div>
      <PageHeader
        title={view.name}
        subtitle={<code className="text-xs">{JSON.stringify(view.filter)}</code>}
      />
      <NodeList nodes={data} loading={isLoading} />
    </div>
  );
}
